import { useSeo } from "../lib/useSeo";
import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Clock, ArrowUpRight, Loader2 } from "lucide-react";
import { ASSETS, LINKS, HOURS, ADDRESS } from "../lib/constants";
import { Reveal } from "../components/Reveal";
import { PageHero, Label, MapBlock } from "../components/bits";
import { sendContact } from "../lib/api";

const field = "w-full border border-white/15 bg-ink px-4 py-4 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-gold";

export default function Contact() {
  useSeo("/contact");
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        label="Visit Us"
        title="Contact Higher Ground DC"
        subtitle="Questions about products, patient registration, or events? Our expert staff is here to help."
        image={ASSETS.exteriorWide}
        testid="contact-hero"
      />

      <section className="bg-ink py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-12">
          <div>
            <Reveal><Label>Get in Touch</Label></Reveal>
            <div className="mt-8 space-y-6">
              <a href={LINKS.google} target="_blank" rel="noopener noreferrer" data-testid="contact-map" className="group flex items-start gap-4 border border-white/10 bg-ink-2 p-6 transition-colors hover:border-gold/50">
                <MapPin className="mt-1 h-5 w-5 text-gold" />
                <div>
                  <p className="text-sm uppercase tracking-[0.15em] text-white">Location</p>
                  <p className="mt-1 text-white/55">{ADDRESS.line1}, {ADDRESS.line2}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-gold">Open in Google Maps <ArrowUpRight className="h-3.5 w-3.5" /></span>
                </div>
              </a>
              <a href={`tel:${LINKS.phone}`} data-testid="contact-phone" className="group flex items-start gap-4 border border-white/10 bg-ink-2 p-6 transition-colors hover:border-gold/50">
                <Phone className="mt-1 h-5 w-5 text-gold" />
                <div>
                  <p className="text-sm uppercase tracking-[0.15em] text-white">Phone</p>
                  <p className="mt-1 text-white/55">(202) 800-1135</p>
                </div>
              </a>
              <div className="flex items-start gap-4 border border-white/10 bg-ink-2 p-6">
                <Clock className="mt-1 h-5 w-5 text-gold" />
                <div>
                  <p className="text-sm uppercase tracking-[0.15em] text-white">Store Hours</p>
                  <p className="mt-1 text-white/55">{HOURS}</p>
                </div>
              </div>
            </div>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="border border-white/10 bg-ink-2 p-8 md:p-10" data-testid="contact-form">
              <h2 className="font-display text-2xl font-light uppercase tracking-[0.1em] text-white">Send a Message</h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <input className={field} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="contact-name" />
                <input className={field} type="email" placeholder="Email address" value={form.email} onChange={set("email")} data-testid="contact-email" />
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <input className={field} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                <select className={field} value={form.subject} onChange={set("subject")} data-testid="contact-subject">
                  <option>General Inquiry</option>
                  <option>Products & Menu</option>
                  <option>Patient Registration</option>
                  <option>Events & Community</option>
                  <option>Wholesale / Partnership</option>
                </select>
              </div>
              <textarea className={`${field} mt-4 min-h-[150px] resize-none`} placeholder="How can we help?" value={form.message} onChange={set("message")} data-testid="contact-message" />
              <button type="submit" disabled={loading} data-testid="contact-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <MapBlock label="Visit Us" />
    </div>
  );
}
